import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AgentsList from './AgentsList';
import NewAgentModal from './components/NewAgentModal';
import CloneAgentModal from './components/CloneAgentModal';
import { agentsService, friendlyError } from '../../services/agentsService';
import { useAuth } from '../../context/AuthContext';
import { toastSuccess, toastWarning } from '../../utils/toast';

export default function AgentsPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const employeeProject = currentUser?.role === 'employee' ? currentUser.employeeProject : null;
  const [agents, setAgents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [models, setModels] = useState([]);
  const [types, setTypes] = useState([]);
  const [publicCreds, setPublicCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const location = useLocation();
  // Desde Inicio, «Nuevo agente» llega con state.create: abre el diálogo directo
  const [creating, setCreating] = useState(Boolean(location.state?.create));
  const [cloning, setCloning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [a, p, m, t, pc] = await Promise.all([agentsService.listAgents(), agentsService.listProjects(), agentsService.listModels(), agentsService.listProjectTypes(),
        agentsService.listPublicCredentials().catch(() => [])]);   // sin el script 42 la lista viene sin «pública»: simplemente no hay claves públicas que ofrecer
      setAgents(a); setProjects(p); setModels(m); setTypes(t); setPublicCreds(pc);
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Proyecto nuevo (si hace falta) y luego el agente. Si el agente falla, el proyecto ya existe: se avisa para no duplicarlo.
  const create = async ({ project, ...agent }) => {
    let projectId = project.id;
    let createdProject = null;
    if (!projectId) {
      try { createdProject = await agentsService.createProjectQuick(project); projectId = createdProject.id; }
      catch (e) { throw new Error(`No se pudo crear el proyecto: ${friendlyError(e)}`); }
    }
    try {
      const { credentialId, ...row } = agent;
      const id = await agentsService.createAgent({ ...row, projectId });
      toastSuccess(createdProject ? 'Proyecto y agente creados' : 'Agente creado');
      // La clave pública se asigna después de crear: si falla, el agente ya existe y se avisa (se puede elegir en la sección Clave).
      if (credentialId) {
        try { await agentsService.useCredential(id, credentialId); }
        catch (e) { toastWarning(`El agente se creó, pero no se pudo asignar la clave: ${friendlyError(e)}`); }
      }
      navigate(`/agentes/${id}`);
    } catch (e) {
      if (createdProject) {
        setProjects((ps) => [...ps, createdProject]);
        throw new Error(`El proyecto «${createdProject.name}» se creó, pero el agente no: ${friendlyError(e)} Elige «Uno que ya existe» para no duplicarlo.`);
      }
      throw new Error(friendlyError(e));
    }
  };

  return (
    <>
      <AgentsList agents={agents} projects={projects} loading={loading} error={error} onRetry={load} onNew={() => setCreating(true)} onClone={() => setCloning(true)} />
      <CloneAgentModal open={cloning} onClose={() => setCloning(false)} agents={agents} />
      <NewAgentModal open={creating} onClose={() => setCreating(false)} projects={projects} types={types} models={models} onCreate={create} publicCredentials={publicCreds} employeeProject={employeeProject} />
    </>
  );
}
